// Admin-only signup funnel: affiliate clicks -> connected accounts -> traders.
import { useCallback, useEffect, useState } from "react";
import { Filter, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";

const Stage = ({ label, value, pct, testid }) => (
  <div data-testid={testid} className="flex-1 min-w-[110px] rounded-lg border border-vts-line bg-black/30 p-3">
    <p className="text-[10px] uppercase tracking-wider text-vts-silver/50">{label}</p>
    <p className="text-xl font-bold text-white font-mono">{value}</p>
    {pct != null && <p className={`text-[10px] font-mono ${pct >= 50 ? "text-vts-profit" : pct >= 20 ? "text-amber-400" : "text-vts-loss"}`}>{pct}% of previous</p>}
  </div>
);

export default function SignupFunnelCard({ onUnauthorized }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/signup-funnel?days=${days}`, { headers: authHeader() });
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) onUnauthorized?.();
      setData({ clicks: 0, connected: 0, traders: 0 });
    }
  }, [days, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  // hide the % when the earlier stage predates tracking (e.g. accounts that
  // connected before click telemetry existed would show ">100%")
  const pct = (a, b) => (b > 0 && a <= b ? Math.round((a / b) * 100) : null);

  return (
    <div data-testid="admin-signup-funnel" className="mt-6 rounded-xl border border-vts-line bg-vts-panel/60 p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Filter className="h-4 w-4 text-vts-blue" />
        <span className="text-[14px] font-bold text-white">Signup Funnel</span>
        <span className="text-[10px] text-vts-silver/50">where new users drop off · Deriv-side signups live in your Deriv affiliate dashboard</span>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`funnel-days-${d}`}
              className={`px-2 py-0.5 rounded text-[10px] font-mono border ${days === d ? "border-vts-blue bg-vts-blue/20 text-white" : "border-vts-line text-vts-silver/60"}`}>{d}d</button>
          ))}
        </div>
      </div>
      {data === null ? (
        <Loader2 className="h-4 w-4 animate-spin text-vts-silver/50" />
      ) : (
        <div className="flex gap-2 flex-wrap">
          <Stage label="Affiliate clicks" value={data.clicks} testid="funnel-clicks" />
          <Stage label="Connected accounts" value={data.connected} pct={pct(data.connected, data.clicks)} testid="funnel-connected" />
          <Stage label="Placed a trade" value={data.traders} pct={pct(data.traders, data.connected)} testid="funnel-traders" />
        </div>
      )}
    </div>
  );
}
